/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useContext } from 'react'

// @mui core components
import {
  Box,
  Card,
  Grid,
  Typography,
  Button,
  Tooltip,
  IconButton,
  Fade,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import HelpIcon from '@mui/icons-material/Help'

// Settings page components
import FormField from 'components/FormField'

// Data
import { IntakeContext } from 'context/IntakeContext'
import { selectData } from 'layouts/onboarding'

// Popup
import { toast } from 'react-toastify'
import { renderMuiFormField } from 'utils/forms/renderMuiFormField'

// Debounce
import { useFormDebounce } from 'hooks/useFormDebounce'
import styled, { keyframes } from 'styled-components'
import LoopIcon from '@mui/icons-material/Loop'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'

const rotatingIcon = keyframes`
  0% {
    transform: rotate(360deg);
  }
  100% {
    transform: rotate(0deg);
  }
`
const colorChangeIcon = keyframes`
  0% {
    color: #66d432;
  }
  50% {
    color: #66d432;
  }
  100% {
    color: #344767;
  }
`
const ChangesSavedIcon = styled(CheckCircleOutlineIcon)`
  &.saved {
    color: #344767;
    font-size: 1.5rem !important;
    animation: 2s ${colorChangeIcon} ease-out;
  }
`
const SavingIcon = styled(LoopIcon)`
  &.loading {
    color: #344767;
    font-size: 1.5rem !important;
    animation: 1.5s ${rotatingIcon} linear infinite;
  }
`

function Other() {
  const { intakeState, updateIntakeStateAtKey, updateUser } =
    useContext(IntakeContext)

  const { updateIntakeStateAndSubmit, loading } = useFormDebounce()

  const showSurvivorBenefit = intakeState?.familyMaritalStatus === 'married'
  const showSurvivorBenefitOwed = intakeState?.familyDivorcedOwedPension

  // add an onSubmit button and call the context function `updateUser`
  // const onSubmit = () => updateUser()
  const onSubmit = async () => {
    await updateUser()
    toast.info('You have successfully updated your Other details', {
      position: 'top-center',
      autoClose: 5000,
      pauseOnFocusLoss: false,
      pauseOnHover: false,
    })
  }

  return (
    <Card
      sx={{
        textAlign: 'left',
        boxShadow: 0,
        border: '1px solid #ddd',
        borderRadius: 2,
      }}
    >
      <Box px={3} pt={3} display="flex" justifyContent="space-between">
        <Typography variant="h3" fontSize={{ xs: '24px', md: '30px' }}>
          Other
        </Typography>

        <Button
          variant="gradient"
          color="dark"
          sx={{ height: '100%' }}
          onClick={onSubmit}
          startIcon={
            loading ? (
              <>
                <SavingIcon fontSize="medium" className={'loading'} />
              </>
            ) : (
              <>
                <ChangesSavedIcon fontSize="medium" className={'saved'} />
              </>
            )
          }
        >
          {loading ? `updating...` : 'up to date'}
        </Button>
      </Box>
      <Box component="form" pb={3} px={3}>
        <Grid container columnSpacing={3} rowSpacing={2}>
          <Grid item xs={12} sm={6}>
            {renderMuiFormField({
              autoFocus: false,
              id: 'giftCard',
              label: 'Preferred Gift Card',
              placeholder: '',
              inputType: 'select',
              value: intakeState.giftCard,
              onChange: updateIntakeStateAndSubmit((giftCard) =>
                updateIntakeStateAtKey({ giftCard })
              ),
              options: selectData.giftCards,
            })}
          </Grid>
        </Grid>
      </Box>
    </Card>
  )
}

export default Other
